import React, { useState, useEffect, useMemo } from 'react'
import { useAppSelector, useAppDispatch } from '../redux/hooks'
import EmailContentViewer from '../components/EmailContentViewer'
import {
  fetchCommunications,
  fetchCommunicationStats,
  fetchEnhancedStats,
  searchCommunications,
  resendCommunication,
  exportCommunications,
  setFilters,
  clearFilters,
  setSearchQuery,
  setPagination,
  clearError,
  clearSuccessMessage,
} from '../redux/reducers/communicationsSlice'
import {
  Mail,
  Search,
  Filter,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RotateCw,
  Copy,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Phone,
  Bell,
  Globe,
  Settings,
  TrendingUp,
  TrendingDown,
  AlertCircle,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import Pagination from '../components/Pagination'

// Type badge colors
const getTypeBadgeClass = (type: string) => {
  switch (type) {
    case 'email':
      return 'bg-blue-100 text-blue-700'
    case 'sms':
      return 'bg-green-100 text-green-700'
    case 'push':
      return 'bg-purple-100 text-purple-700'
    case 'webhook':
      return 'bg-orange-100 text-orange-700'
    case 'system':
      return 'bg-gray-100 text-gray-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

// Status badge colors
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'bg-green-100 text-green-700'
    case 'failed':
    case 'bounced':
      return 'bg-red-100 text-red-700'
    case 'pending':
    case 'queued':
      return 'bg-yellow-100 text-yellow-700'
    case 'retry':
      return 'bg-orange-100 text-orange-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

// Environment badge colors
const getEnvironmentBadgeClass = (env: string) => {
  switch (env) {
    case 'production':
      return 'bg-red-100 text-red-700 font-semibold'
    case 'development':
      return 'bg-blue-100 text-blue-700'
    case 'staging':
      return 'bg-yellow-100 text-yellow-700'
    case 'test':
      return 'bg-gray-100 text-gray-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

// Type icon component
const TypeIcon: React.FC<{ type: string }> = ({ type }) => {
  switch (type) {
    case 'email':
      return <Mail className="w-4 h-4" />
    case 'sms':
      return <Phone className="w-4 h-4" />
    case 'push':
      return <Bell className="w-4 h-4" />
    case 'webhook':
      return <Globe className="w-4 h-4" />
    case 'system':
      return <Settings className="w-4 h-4" />
    default:
      return <MessageSquare className="w-4 h-4" />
  }
}

const CommunicationManagement: React.FC = () => {
  const dispatch = useAppDispatch()
  const { 
    communications, 
    stats, 
    enhancedStats,
    loading, 
    statsLoading,
    error, 
    pagination, 
    filters, 
    searchQuery: storeSearchQuery,
    successMessage 
  } = useAppSelector((state) => state.communications)
  
  const [localSearchQuery, setLocalSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('all')
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Initial fetch
  useEffect(() => {
    dispatch(fetchCommunications({ 
      filters: {}, 
      pagination: { skip: 0, limit: 20 } 
    }))
    dispatch(fetchCommunicationStats())
    dispatch(fetchEnhancedStats())
  }, [dispatch])

  // Auto-dismiss messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage())
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage, dispatch])

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError())
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [error, dispatch])

  // Handle search
  const handleSearch = () => {
    if (localSearchQuery.trim()) {
      dispatch(searchCommunications({
        query: localSearchQuery,
        filters: {
          ...(selectedType !== 'all' && { type: selectedType }),
          ...(selectedStatus !== 'all' && { status: selectedStatus }),
          ...(selectedEnvironment !== 'all' && { environment: selectedEnvironment }),
        },
        pagination: { skip: 0, limit: pagination.limit }
      }))
    } else {
      handleFilterChange()
    }
  }

  // Handle filter change
  const handleFilterChange = () => {
    const newFilters: any = {}
    if (selectedType !== 'all') newFilters.type = selectedType
    if (selectedStatus !== 'all') newFilters.status = selectedStatus
    if (selectedEnvironment !== 'all') newFilters.environment = selectedEnvironment

    dispatch(setFilters(newFilters))
    dispatch(fetchCommunications({ 
      filters: newFilters, 
      pagination: { skip: 0, limit: pagination.limit } 
    }))
  }

  // Use effect to handle filter changes
  useEffect(() => {
    if (selectedType || selectedStatus || selectedEnvironment) {
      handleFilterChange()
    }
  }, [selectedType, selectedStatus, selectedEnvironment])

  // Handle resend
  const handleResend = async (id: string) => {
    await dispatch(resendCommunication(id))
    // Refresh the list
    dispatch(fetchCommunications({ 
      filters, 
      pagination: { skip: pagination.skip, limit: pagination.limit } 
    }))
  }


  // Handle export
  const handleExport = (format: 'csv' | 'json') => {
    dispatch(exportCommunications({ format, filters }))
    setShowExportMenu(false)
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    const newSkip = (page - 1) * pagination.limit
    dispatch(setPagination({ skip: newSkip }))
    dispatch(fetchCommunications({ 
      filters, 
      pagination: { skip: newSkip, limit: pagination.limit } 
    }))
  }

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    // Could show a toast notification here
  }

  // Calculate current page
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1
  const totalPages = Math.ceil(pagination.total / pagination.limit)

  return (
    <div className="space-y-4 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="flex items-center space-x-2">
          <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Communications</h1>
        </div>
        

        {/* Export Button */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
            <ChevronDown className="w-4 h-4" />
          </button>
          
          {showExportMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
              <button
                onClick={() => handleExport('csv')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                Export as CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                Export as JSON
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 uppercase font-medium">Total</div>
            <div className="text-xl sm:text-2xl font-semibold mt-1">{stats.total || 0}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 uppercase font-medium">Sent</div>
            <div className="text-xl sm:text-2xl font-semibold mt-1 text-green-600">{stats.sent || 0}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 uppercase font-medium">Failed</div>
            <div className="text-xl sm:text-2xl font-semibold mt-1 text-red-600">{stats.failed || 0}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 uppercase font-medium">Success Rate</div>
            <div className="text-xl sm:text-2xl font-semibold mt-1">
              {stats.total > 0 ? ((stats.sent / stats.total) * 100).toFixed(1) : 0}%
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 space-y-3">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Type Filter - Mobile: Dropdown, Desktop: Buttons */}
          <div className="w-full lg:w-auto">
            {/* Mobile Dropdown */}
            <select
              className="lg:hidden w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value)
                handleFilterChange()
              }}
            >
              {['all', 'email', 'sms', 'push', 'webhook', 'system'].map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : type.toUpperCase()}
                </option>
              ))}
            </select>
            
            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center flex-wrap gap-1">
              {['all', 'email', 'sms', 'push', 'webhook', 'system'].map(type => (
                <button
                  key={type}
                  onClick={() => {
                    setSelectedType(type)
                    handleFilterChange()
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    selectedType === type
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type === 'all' ? 'All Types' : type.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Advanced Filters */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
          >
            <Filter className="w-4 h-4" />
            <span>Filters</span>
            <ChevronDown className={`w-4 h-4 transform transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="pt-3 border-t border-gray-200 flex flex-col sm:flex-row gap-2">
            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value)
                handleFilterChange()
              }}
              className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Status</option>
              <option value="sent">Sent</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="queued">Queued</option>
              <option value="bounced">Bounced</option>
              <option value="retry">Retry</option>
            </select>

            {/* Environment Filter */}
            <select
              value={selectedEnvironment}
              onChange={(e) => {
                setSelectedEnvironment(e.target.value)
                handleFilterChange()
              }}
              className="w-full sm:w-auto px-3 py-1.5 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Environments</option>
              <option value="development">Development</option>
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="test">Test</option>
            </select>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setSelectedType('all')
                setSelectedStatus('all')
                setSelectedEnvironment('all')
                setLocalSearchQuery('')
                dispatch(clearFilters())
                dispatch(fetchCommunications({ 
                  filters: {}, 
                  pagination: { skip: 0, limit: pagination.limit } 
                }))
              }}
              className="w-full sm:w-auto px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md sm:border-0"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Table - Hidden on Mobile, Card View on Mobile */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject / Content
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Environment
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading communications...
                  </td>
                </tr>
              ) : communications.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No communications found
                  </td>
                </tr>
              ) : (
                communications.map((comm) => (
                  <React.Fragment key={comm._id}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === comm._id ? null : comm._id)}
                    >
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {format(new Date(comm.createdAt), 'HH:mm:ss')}
                        <div className="text-xs text-gray-400">
                          {format(new Date(comm.createdAt), 'MMM dd')}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${getTypeBadgeClass(comm.type)}`}>
                          <TypeIcon type={comm.type} />
                          <span>{comm.type}</span>
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="text-gray-900">
                          {comm.recipient.email || comm.recipient.phone || comm.recipient.deviceToken || 'N/A'}
                        </div>
                        {comm.recipient.name && (
                          <div className="text-xs text-gray-500">{comm.recipient.name}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <span>
                            {comm.metadata?.subject || comm.content?.text?.substring(0, 50) || 'No content'}
                            {comm.content?.text && comm.content.text.length > 50 && '...'}
                          </span>
                          {comm.content?.attachments && comm.content.attachments.length > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              📎 {comm.content.attachments.length}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getEnvironmentBadgeClass(comm.metadata?.environment || 'unknown')}`}>
                          {comm.metadata?.environment || 'unknown'}
                        </span>
                        {comm.metadata?.isTest && (
                          <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            TEST
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getStatusBadgeClass(comm.status)}`}>
                          {comm.status === 'sent' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {comm.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                          {comm.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                          {comm.status}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setExpandedRow(expandedRow === comm._id ? null : comm._id)
                            }}
                            className="text-gray-400 hover:text-gray-600"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {comm.status === 'failed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleResend(comm._id)
                              }}
                              className="text-gray-400 hover:text-blue-600"
                              title="Resend"
                            >
                              <RotateCw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              copyToClipboard(comm._id)
                            }}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy ID"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    
                    {/* Expanded Row Details */}
                    {expandedRow === comm._id && (
                      <tr>
                        <td colSpan={7} className="px-4 py-4 bg-gray-50">
                          <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">ID:</span>{' '}
                                <span className="font-mono text-xs">{comm._id}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Provider:</span>{' '}
                                <span>{comm.provider || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Retry Count:</span>{' '}
                                <span>{comm.retryCount || 0}</span>
                              </div>
                            </div>
                            
                            {comm.error && (
                              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                <div className="text-sm font-medium text-red-800 mb-1">Error Details</div>
                                <div className="text-sm text-red-700">{comm.error.message}</div>
                                {comm.error.code && (
                                  <div className="text-xs text-red-600 mt-1">Code: {comm.error.code}</div>
                                )}
                              </div>
                            )}
                            
                            {comm.content && (
                              <EmailContentViewer 
                                content={comm.content} 
                                metadata={comm.metadata}
                              />
                            )}
                            
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              <div>
                                Created: {format(new Date(comm.createdAt), 'PPpp')}
                              </div>
                              {comm.timestamps?.sent && (
                                <div>
                                  Sent: {format(new Date(comm.timestamps.sent), 'PPpp')}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Mobile Card View */}
        <div className="lg:hidden">
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading communications...
            </div>
          ) : communications.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              No communications found
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {communications.map((comm) => (
                <div 
                  key={comm._id} 
                  className="p-4 hover:bg-gray-50"
                  onClick={() => setExpandedRow(expandedRow === comm._id ? null : comm._id)}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-xs font-medium ${getTypeBadgeClass(comm.type)}`}>
                        <TypeIcon type={comm.type} />
                        <span>{comm.type}</span>
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getStatusBadgeClass(comm.status)}`}>
                        {comm.status === 'sent' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {comm.status === 'failed' && <XCircle className="w-3 h-3 mr-1" />}
                        {comm.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                        {comm.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {format(new Date(comm.createdAt), 'HH:mm')}
                      <div>{format(new Date(comm.createdAt), 'MMM dd')}</div>
                    </div>
                  </div>
                  
                  {/* Card Body */}
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="text-gray-500">To: </span>
                      <span className="text-gray-900">
                        {comm.recipient.email || comm.recipient.phone || comm.recipient.deviceToken || 'N/A'}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-700">
                      {comm.metadata?.subject || comm.content?.text?.substring(0, 100) || 'No content'}
                      {comm.content?.text && comm.content.text.length > 100 && '...'}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getEnvironmentBadgeClass(comm.metadata?.environment || 'unknown')}`}>
                        {comm.metadata?.environment || 'unknown'}
                      </span>
                      {comm.metadata?.isTest && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          TEST
                        </span>
                      )}
                      {comm.content?.attachments && comm.content.attachments.length > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          📎 {comm.content.attachments.length}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Card Actions */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedRow(expandedRow === comm._id ? null : comm._id)
                      }}
                      className="text-blue-600 text-sm font-medium flex items-center space-x-1"
                    >
                      <Eye className="w-4 h-4" />
                      <span>View Details</span>
                    </button>
                    
                    <div className="flex items-center space-x-3">
                      {comm.status === 'failed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleResend(comm._id)
                          }}
                          className="text-gray-400 hover:text-blue-600"
                          title="Resend"
                        >
                          <RotateCw className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          copyToClipboard(comm._id)
                        }}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copy ID"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedRow === comm._id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      <div className="text-sm">
                        <span className="text-gray-500">ID:</span>{' '}
                        <span className="font-mono text-xs">{comm._id}</span>
                      </div>
                      
                      {comm.provider && (
                        <div className="text-sm">
                          <span className="text-gray-500">Provider:</span> {comm.provider}
                        </div>
                      )}
                      
                      {comm.error && (
                        <div className="bg-red-50 border border-red-200 rounded-md p-3">
                          <div className="text-sm font-medium text-red-800 mb-1">Error Details</div>
                          <div className="text-sm text-red-700">{comm.error.message}</div>
                          {comm.error.code && (
                            <div className="text-xs text-red-600 mt-1">Code: {comm.error.code}</div>
                          )}
                        </div>
                      )}
                      
                      {comm.content && (
                        <EmailContentViewer 
                          content={comm.content} 
                          metadata={comm.metadata}
                        />
                      )}
                      
                      <div className="text-xs text-gray-500">
                        <div>Created: {format(new Date(comm.createdAt), 'PPpp')}</div>
                        {comm.timestamps?.sent && (
                          <div>Sent: {format(new Date(comm.timestamps.sent), 'PPpp')}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-3 sm:px-4 py-3 border-t border-gray-200">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default CommunicationManagement